import type { CalibreCacheSnapshot } from '@watchkeeper/shared-types';
import type { CalibreTier, CalibreTierOverrides } from '../../services/firebase';
import { OverrideFieldsGrid } from './CalibreTiersEditor';
import {
  applyTierOverrides,
  effectiveAccuracy,
  displayOverrideLookupValue,
  isAccuracyKey,
} from './calibreTiers';
import { cacheToBaseData, countCalibreOverrides } from './refCalibreCache';

/**
 * Per-ref `calibreOverrides` editor (Phase 3a). Renders the SAME override
 * control grid as the tier editor (via {@link OverrideFieldsGrid}), treating the
 * ref's `calibreCache` as the base. Below it, a live "effective" preview shows
 * cache + overrides resolved per field (overridden values badged).
 *
 * Legacy-value migration is now automatic (diverging `movement.*` values are
 * copied into overrides on link/save by the ref editor), so this component no
 * longer renders the old interactive "Move to override" panel.
 */
export function RefCalibreOverridesEditor({
  cache,
  overrides,
  onOverrideChange,
  resolveLookupLabel,
}: {
  cache: CalibreCacheSnapshot | undefined;
  overrides: CalibreTierOverrides;
  onOverrideChange: <K extends keyof CalibreTierOverrides>(
    key: K,
    value: CalibreTierOverrides[K],
  ) => void;
  resolveLookupLabel: (collection: string, raw: string) => string;
}) {
  const baseData = cacheToBaseData(cache);
  // The per-ref overrides resolve exactly like a tier's overrides over the base.
  const syntheticTier: CalibreTier = { id: '', name: '', overrides };
  const effective = applyTierOverrides(baseData, syntheticTier);
  const effectiveNonAccuracy = effective.filter((f) => !isAccuracyKey(f.key));
  const acc = effectiveAccuracy(baseData, syntheticTier);
  const hasOverrides = countCalibreOverrides(overrides) > 0;

  return (
    <div className='mt-2 border rounded-lg p-3 bg-white space-y-3'>
      <div>
        <p className='text-xs text-gray-400 mb-2'>
          Layer per-reference values on top of the Calibre Entry. Leave a field
          on <em>Inherit</em> / blank to use the linked calibre's effective
          value. Only fields set here override the Calibre Entry at render time.
        </p>
        <OverrideFieldsGrid
          baseData={baseData}
          overrides={overrides}
          onChange={onOverrideChange}
          overrideLabel='calibre'
        />
      </div>

      {/* Live effective spec = Calibre Entry overlaid with the per-ref
          overrides. Only rendered when at least one override is set — with none,
          it's redundant with the primary Effective Calibre Spec panel above. */}
      {hasOverrides && (
      <div className='border rounded-lg p-2 bg-gray-50'>
        <div className='text-[11px] font-medium text-gray-500 uppercase mb-1.5'>
          Effective (Calibre Entry + Overrides)
        </div>
        <div className='grid grid-cols-2 gap-x-3 gap-y-1'>
          {effectiveNonAccuracy.map((f) => (
            <div key={f.key} className='flex items-center gap-1.5 text-xs'>
              <span className='text-gray-500 truncate'>{f.label}</span>
              <span
                className={`ml-auto font-medium ${
                  f.overridden ? 'text-brand-700' : 'text-gray-700'
                }`}
              >
                {displayOverrideLookupValue(f.key, f.value, resolveLookupLabel)}
              </span>
              {f.overridden && (
                <span className='shrink-0 rounded bg-brand-100 text-brand-700 px-1 py-0.5 text-[9px] font-semibold uppercase'>
                  ref
                </span>
              )}
            </div>
          ))}
          <div className='flex items-center gap-1.5 text-xs'>
            <span className='text-gray-500 truncate'>Accuracy</span>
            <span
              className={`ml-auto font-medium ${
                acc.overridden ? 'text-brand-700' : 'text-gray-700'
              }`}
            >
              {acc.display}
            </span>
            {acc.overridden && (
              <span className='shrink-0 rounded bg-brand-100 text-brand-700 px-1 py-0.5 text-[9px] font-semibold uppercase'>
                ref
              </span>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
